import { useEffect, useRef, useState, useMemo } from "react";
import { BufferGeometry, Float32BufferAttribute } from "three";
import { PLYLoader } from "three/examples/jsm/loaders/PLYLoader.js";
import type { CenterlineData } from "./useTunnelCenterline";
import { useProject } from "./ProjectContext";
import { fetchWithCache } from "./usePointcloudCache";

interface ScanData {
  metadata: { scanner: string; date: string; description: string; crs: string; pointCount: number };
  stride: number;
  data: number[];
}

type Offsets = { avgE: number; avgN: number; avgZ: number };

/**
 * Renders point clouds from the active project's configured endpoints.
 * Each scan is loaded independently and cached. Only scans whose index
 * appears in `enabledIndices` are rendered.
 */
export function PointCloud3D({ data, enabledIndices, centerlineLock, onLoadingChange, stereonetFilter, stereonetFilterIndex }: {
  data: CenterlineData;
  enabledIndices: ReadonlySet<number>;
  centerlineLock?: boolean;
  onLoadingChange?: (loadingIndices: Set<number>) => void;
  stereonetFilter?: { x: number; y: number; r: number };
  stereonetFilterIndex?: number;
}) {
  const project = useProject();
  const [offsets, setOffsets] = useState<Offsets | null>(null);
  // Cache: index -> geometry (persists across toggle on/off)
  const cache = useRef<Map<number, BufferGeometry>>(new Map());
  const [loaded, setLoaded] = useState<Map<number, BufferGeometry>>(new Map());
  const loadingRef = useRef<Set<number>>(new Set());

  // Fetch centerline offsets
  useEffect(() => {
    setOffsets(null);
    cache.current = new Map();
    setLoaded(new Map());
    fetch(project.pointcloudCenterlineEndpoint)
      .then((r) => r.text())
      .then((xml) => setOffsets(computeCenterlineAverages(xml)))
      .catch(() => {});
  }, [project.pointcloudCenterlineEndpoint]);

  // Point cloud UTM offset (for relocated demo projects)
  const pcOffset = project.pointcloudUtmOffset ?? [0, 0, 0];

  // Load individual scans on demand
  useEffect(() => {
    if (!offsets) return;
    const plyLoader = new PLYLoader();
    let cancelled = false;

    for (const idx of enabledIndices) {
      if (cache.current.has(idx)) {
        // Already loaded — make sure it's in the rendered set
        if (!loaded.has(idx)) {
          setLoaded((prev) => new Map(prev).set(idx, cache.current.get(idx)!));
        }
        continue;
      }

      const ep = project.pointcloudEndpoints[idx];
      if (!ep) continue;

      (async () => {
        loadingRef.current.add(idx);
        onLoadingChange?.(new Set(loadingRef.current));
        try {
          let geom: BufferGeometry;
          if (ep.format === "json") {
            const raw = await fetchWithCache(ep.url, "json");
            const scan: ScanData = typeof raw === "string" ? JSON.parse(raw) : JSON.parse(new TextDecoder().decode(raw as ArrayBuffer));
            geom = buildGeometryFromJSON(scan, offsets, pcOffset);
          } else {
            const raw = await fetchWithCache(ep.url, "ply");
            const buf = raw instanceof ArrayBuffer ? raw : new TextEncoder().encode(raw as string).buffer;
            const plyGeom = plyLoader.parse(buf);
            geom = buildGeometryFromPLY(plyGeom, offsets, pcOffset);
          }
          if (cancelled) return;
          cache.current.set(idx, geom);
          setLoaded((prev) => new Map(prev).set(idx, geom));
        } catch (e) {
          console.error(`Failed to load point cloud ${ep.label}:`, e);
        } finally {
          loadingRef.current.delete(idx);
          onLoadingChange?.(new Set(loadingRef.current));
        }
      })();
    }

    return () => { cancelled = true; };
  }, [offsets, enabledIndices, project.pointcloudEndpoints]);

  // Apply stereonet color filter: dim points outside the selected region (only for the analysis point cloud)
  const filteredGeometries = useMemo(() => {
    if (!stereonetFilter || stereonetFilterIndex == null) return null;
    const geom = loaded.get(stereonetFilterIndex);
    if (!geom) return null;

    const result = new Map<number, BufferGeometry>();
    const selCx = stereonetFilter.x - 0.5;
    const selCy = stereonetFilter.y - 0.5;
    const selR = stereonetFilter.r;
    const selDist = Math.sqrt(selCx * selCx + selCy * selCy);

    // Compute wrapped center (mirrored through the boundary on opposite side)
    const needsWrap = selDist + selR > 0.5;
    let wrapCx = 0, wrapCy = 0;
    if (needsWrap && selDist > 0.001) {
      const wrapDist = 1.0 - selDist;
      wrapCx = -selCx / selDist * wrapDist;
      wrapCy = -selCy / selDist * wrapDist;
    }

    const colorAttr = geom.getAttribute("color");
    if (!colorAttr) return null;
    const count = colorAttr.count;
    const newColors = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      const r = colorAttr.getX(i);
      const g = colorAttr.getY(i);
      const b = colorAttr.getZ(i);
      const { sx, sy } = rgbToStereonetPos(r, g, b);

      const dx = sx - selCx;
      const dy = sy - selCy;
      const dist = Math.sqrt(dx * dx + dy * dy);

      let wrapDistVal = Infinity;
      if (needsWrap) {
        const wdx = sx - wrapCx;
        const wdy = sy - wrapCy;
        wrapDistVal = Math.sqrt(wdx * wdx + wdy * wdy);
      }

      if (dist <= selR || wrapDistVal <= selR) {
        newColors[i * 3] = r;
        newColors[i * 3 + 1] = g;
        newColors[i * 3 + 2] = b;
      } else {
        newColors[i * 3] = 0;
        newColors[i * 3 + 1] = 0;
        newColors[i * 3 + 2] = 0;
      }
    }
    const newGeom = geom.clone();
    newGeom.setAttribute("color", new Float32BufferAttribute(newColors, 3));
    result.set(stereonetFilterIndex, newGeom);
    return result;
  }, [stereonetFilter, stereonetFilterIndex, loaded]);

  if (enabledIndices.size === 0) return null;

  return (
    <>
      {Array.from(enabledIndices).map((idx) => {
        const geom = (filteredGeometries && filteredGeometries.has(idx)) ? filteredGeometries.get(idx)! : loaded.get(idx);
        if (!geom) {
          return null;
        }
        return (
          <points key={`${idx}-${stereonetFilter && idx === stereonetFilterIndex ? `${stereonetFilter.x.toFixed(3)}-${stereonetFilter.y.toFixed(3)}-${stereonetFilter.r.toFixed(3)}` : "none"}`} geometry={geom}>
            <pointsMaterial size={centerlineLock ? 0.03 : 0.08} vertexColors sizeAttenuation />
          </points>
        );
      })}
    </>
  );
}

/** Build geometry from the JSON scan format (e16-fas) */
function buildGeometryFromJSON(scan: ScanData, offsets: Offsets, utmOffset: [number, number, number]): BufferGeometry {
  const s = scan.stride;
  const count = scan.data.length / s;
  const positions = new Float32Array(count * 3);
  const colors = new Float32Array(count * 3);

  for (let i = 0; i < count; i++) {
    const e = scan.data[i * s] + utmOffset[0];
    const n = scan.data[i * s + 1] + utmOffset[1];
    const z = scan.data[i * s + 2] + utmOffset[2];
    // Same mapping as parseLandXML: X = easting - avgE, Y = elev - avgZ, Z = northing - avgN
    positions[i * 3] = e - offsets.avgE;
    positions[i * 3 + 1] = z - offsets.avgZ;
    positions[i * 3 + 2] = n - offsets.avgN;

    colors[i * 3] = scan.data[i * s + 3] / 255;
    colors[i * 3 + 1] = scan.data[i * s + 4] / 255;
    colors[i * 3 + 2] = scan.data[i * s + 5] / 255;
  }

  const geom = new BufferGeometry();
  geom.setAttribute("position", new Float32BufferAttribute(positions, 3));
  geom.setAttribute("color", new Float32BufferAttribute(colors, 3));
  return geom;
}

/** Build geometry from a parsed PLY BufferGeometry, re-centering coordinates */
function buildGeometryFromPLY(plyGeom: BufferGeometry, offsets: Offsets, utmOffset: [number, number, number]): BufferGeometry {
  const posAttr = plyGeom.getAttribute("position");
  const count = posAttr.count;
  const positions = new Float32Array(count * 3);
  const colors = new Float32Array(count * 3);

  const hasColor = plyGeom.getAttribute("color") != null;
  const colorAttr = hasColor ? plyGeom.getAttribute("color") : null;

  for (let i = 0; i < count; i++) {
    // PLY coords are georeferenced: x=easting, y=northing, z=elevation
    const e = posAttr.getX(i) + utmOffset[0];
    const n = posAttr.getY(i) + utmOffset[1];
    const z = posAttr.getZ(i) + utmOffset[2];

    // Same mapping as parseLandXML: X = easting - avgE, Y = elev - avgZ, Z = northing - avgN
    positions[i * 3] = e - offsets.avgE;
    positions[i * 3 + 1] = z - offsets.avgZ;
    positions[i * 3 + 2] = n - offsets.avgN;

    if (colorAttr) {
      colors[i * 3] = colorAttr.getX(i);
      colors[i * 3 + 1] = colorAttr.getY(i);
      colors[i * 3 + 2] = colorAttr.getZ(i);
    } else {
      // Gray fallback (e.g. Scan-6.ply has no vertex colors)
      colors[i * 3] = 0.6;
      colors[i * 3 + 1] = 0.6;
      colors[i * 3 + 2] = 0.6;
    }
  }

  const geom = new BufferGeometry();
  geom.setAttribute("position", new Float32BufferAttribute(positions, 3));
  geom.setAttribute("color", new Float32BufferAttribute(colors, 3));
  return geom;
}

/** Compute the centering averages from the centerline XML (same as parseLandXML) */
function computeCenterlineAverages(xml: string): { avgE: number; avgN: number; avgZ: number } {
  const parser = new DOMParser();
  const doc = parser.parseFromString(xml, "application/xml");
  const ns = "http://www.landxml.org/schema/LandXML-1.2";

  const coordGeom = doc.getElementsByTagNameNS(ns, "CoordGeom")[0];
  const hPoints: { station: number; easting: number; northing: number }[] = [];
  let station = 0;

  const parseC = (el: Element) => {
    const parts = (el.textContent || "").trim().split(/\s+/).map(Number);
    return [parts[0], parts[1]] as [number, number];
  };

  for (const child of Array.from(coordGeom.children)) {
    const tag = child.localName;
    if (tag === "Line") {
      const length = parseFloat(child.getAttribute("length") || "0");
      const [sE, sN] = parseC(child.getElementsByTagNameNS(ns, "Start")[0]);
      const [eE, eN] = parseC(child.getElementsByTagNameNS(ns, "End")[0]);
      if (hPoints.length === 0) hPoints.push({ station, easting: sE, northing: sN });
      station += length;
      hPoints.push({ station, easting: eE, northing: eN });
    } else if (tag === "Curve") {
      const length = parseFloat(child.getAttribute("length") || "0");
      const radius = parseFloat(child.getAttribute("radius") || "0");
      const rot = child.getAttribute("rot");
      const [sE, sN] = parseC(child.getElementsByTagNameNS(ns, "Start")[0]);
      const [cE, cN] = parseC(child.getElementsByTagNameNS(ns, "Center")[0]);
      if (hPoints.length === 0) hPoints.push({ station, easting: sE, northing: sN });
      const startAngle = Math.atan2(sN - cN, sE - cE);
      const endAngle = Math.atan2(
        parseC(child.getElementsByTagNameNS(ns, "End")[0])[1] - cN,
        parseC(child.getElementsByTagNameNS(ns, "End")[0])[0] - cE,
      );
      const arcSteps = Math.max(Math.ceil(length / 2), 10);
      for (let i = 1; i <= arcSteps; i++) {
        const t = i / arcSteps;
        let angle: number;
        if (rot === "cw") {
          let sweep = startAngle - endAngle;
          if (sweep < 0) sweep += 2 * Math.PI;
          angle = startAngle - sweep * t;
        } else {
          let sweep = endAngle - startAngle;
          if (sweep < 0) sweep += 2 * Math.PI;
          angle = startAngle + sweep * t;
        }
        station += length / arcSteps;
        hPoints.push({ station, easting: cE + radius * Math.cos(angle), northing: cN + radius * Math.sin(angle) });
      }
    }
  }

  // Vertical profile
  const profAlign = doc.getElementsByTagNameNS(ns, "ProfAlign")[0];
  const pviPoints: { station: number; elevation: number }[] = [];
  for (const child of Array.from(profAlign.children)) {
    const parts = (child.textContent || "").trim().split(/\s+/).map(Number);
    pviPoints.push({ station: parts[0], elevation: parts[1] });
  }

  const interpH = (s: number) => {
    if (s <= hPoints[0].station) return hPoints[0];
    if (s >= hPoints[hPoints.length - 1].station) return hPoints[hPoints.length - 1];
    for (let i = 0; i < hPoints.length - 1; i++) {
      if (s >= hPoints[i].station && s <= hPoints[i + 1].station) {
        const t = (s - hPoints[i].station) / (hPoints[i + 1].station - hPoints[i].station);
        return {
          station: s,
          easting: hPoints[i].easting + t * (hPoints[i + 1].easting - hPoints[i].easting),
          northing: hPoints[i].northing + t * (hPoints[i + 1].northing - hPoints[i].northing),
        };
      }
    }
    return hPoints[hPoints.length - 1];
  };

  const interpElev = (s: number) => {
    if (s <= pviPoints[0].station) return pviPoints[0].elevation;
    if (s >= pviPoints[pviPoints.length - 1].station) return pviPoints[pviPoints.length - 1].elevation;
    for (let i = 0; i < pviPoints.length - 1; i++) {
      if (s >= pviPoints[i].station && s <= pviPoints[i + 1].station) {
        const t = (s - pviPoints[i].station) / (pviPoints[i + 1].station - pviPoints[i].station);
        return pviPoints[i].elevation + t * (pviPoints[i + 1].elevation - pviPoints[i].elevation);
      }
    }
    return pviPoints[pviPoints.length - 1].elevation;
  };

  const totalLength = hPoints[hPoints.length - 1].station;
  let sumE = 0, sumN = 0, sumZ = 0, count = 0;
  for (let s = 0; s <= totalLength; s += 2) {
    const h = interpH(s);
    sumE += h.easting;
    sumN += h.northing;
    sumZ += interpElev(s);
    count++;
  }

  return { avgE: sumE / count, avgN: sumN / count, avgZ: sumZ / count };
}

/**
 * Reverse the Coltop color mapping: given an RGB color (0-1 range),
 * return the stereonet position as normalized coords (-0.5 to 0.5) from center.
 */
function rgbToStereonetPos(r: number, g: number, b: number): { sx: number; sy: number } {
  // RGB to HSV
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const delta = max - min;
  let h = 0;
  if (delta > 0.001) {
    if (max === r) h = 60 * (((g - b) / delta) % 6);
    else if (max === g) h = 60 * ((b - r) / delta + 2);
    else h = 60 * ((r - g) / delta + 4);
    if (h < 0) h += 360;
  }
  const s = max > 0.001 ? delta / max : 0;
  // v = max (should be ~1 for Coltop)

  // Reverse Coltop: h = (540 - dipDir) % 360 => dipDir = (540 - h) % 360
  let dipDir = (540 - h) % 360;
  if (dipDir < 0) dipDir += 360;

  // s = sqrt(2) * sin(dip/2 * pi/180) => dip = 2 * asin(s / sqrt(2)) * 180/pi
  const sinHalfDip = Math.min(1, s / Math.sqrt(2));
  const dip = 2 * Math.asin(sinHalfDip) * 180 / Math.PI;

  // Convert dipDir/dip to stereonet x,y (normalized -0.5 to 0.5)
  // radius from center = dip/90 * 0.5
  const radius = (dip / 90) * 0.5;
  // angle: dipDir 0=N (up), clockwise
  const angle = dipDir * Math.PI / 180;
  const sx = radius * Math.sin(angle);
  const sy = -radius * Math.cos(angle);
  return { sx, sy };
}
